import { useEffect } from "react";
import { Form } from "@formio/react";
import "./styles.css";
import { Skeleton } from "@mui/material";
import { useKeycloak } from "providers/KeycloakProvider";
import { useLocation } from "react-router-dom";
import { ApiError } from "./components/ApiError";
import { Attachments } from "./components/Attachments";
import { FormNotFound } from "./components/FormNotFound";
import { ProcessNotFound } from "./components/ProcessNotFound";
import { useProcessExecutor } from "./useProcessExecutor";
import { LogoAnimation } from "@/components/Logo/logoAnimation";

interface FormViewProps {
  header?: string;
  initialData?: Record<string, unknown>;
  formKey?: string;
  onComplete?: (taskId: string) => void;
  values?: object;
  inpData?: Record<string, unknown>;
}

interface LocationProps {
  state: FormViewProps & {
    from: Location;
  };
}

export const FormView = ({
  header,
  initialData: propsInitialData = {},
  formKey,
  onComplete,
  values,
  inpData,
}: FormViewProps) => {
  // If values is provided and contains a 'contact' property, replace it with 'contactData'

  const processState = useProcessExecutor(formKey, onComplete, values);

  const { state: locationState } = useLocation() as unknown as LocationProps;

  return (
    <>
      {processState.executorState === "READY" &&
        processState.formDefinition && (
          <div className="flex overflow-hidden flex-col h-full w-full">
            <div
              className="overflow-y-auto overflow-x-hidden grow-1"
              id="form-scroll-container"
            >
              <div className="container mx-auto h-full">
                <div className="grid grid-cols-1 gap-4 px-2">
                  {processState.attachments.length > 0 && (
                    <Attachments attachments={processState.attachments} />
                  )}
                  <div className="formio-form">
                    <Form
                      key={processState.taskId}
                      form={
                        processState.formDefinition?.inpData
                          ? processState.formDefinition // inpData already set by backend
                          : inpData
                          ? { ...processState.formDefinition, inpData }
                          : processState.formDefinition
                      }
                      onSubmit={processState.submitData}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      formReady={(form: any) => {
                        const scrollContainer = document.getElementById(
                          "form-scroll-container"
                        );
                        const navigateToTab = (tabHref: string) => {
                          const tabLink =
                            document.querySelector<HTMLAnchorElement>(
                              `a[href='${tabHref}']`
                            );
                          if (tabLink) {
                            tabLink.click();
                            // Scroll the container instead of the window
                            if (scrollContainer) {
                              scrollContainer.scrollTo({
                                top: 0,
                                behavior: "smooth",
                              });
                            }
                          }
                        };
                        const navigationMap: Record<string, string> = {
                          btnNeste: "#tab_mifid",
                          btnTilbake: "#tab_aml",
                        };
                        Object.entries(navigationMap).forEach(
                          ([buttonName, targetTab]) => {
                            const button = document.querySelector(
                              `[name="data[${buttonName}]"]`
                            );
                            if (button) {
                              button.addEventListener("click", () =>
                                navigateToTab(targetTab)
                              );
                            }
                          }
                        );

                        // Auto-switch to MiFID tab when updateType=2
                        const formInpData = (
                          processState.formDefinition as
                            | { inpData?: Record<string, unknown> }
                            | undefined
                        )?.inpData;
                        if (
                          (formInpData?.updateType ?? inpData?.updateType) ===
                          "2"
                        ) {
                          setTimeout(() => {
                            const tab = form.getComponent("cgTabs");
                            if (tab) tab.setTab(1);
                          }, 100);
                        }
                      }}
                      submission={{
                        data: {
                          ...locationState?.initialData,
                          ...propsInitialData,
                          ...(inpData || {}),
                          ...processState.initialData,
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      {processState.executorState === "READY" &&
        !processState.formDefinition && <FormNotFound />}
      {processState.executorState === "LOADING" && (
        <div className="flex items-center justify-center h-full w-full">
          <div className="flex flex-col items-center gap-6 p-8 max-w-md">
            <LogoAnimation size={64} variant="spin" />

            {/* Text content */}
            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold text-gray-900">
                Loading form...
              </h3>
              <p className="text-sm text-gray-500">
                Please wait while we prepare your form
              </p>
            </div>
          </div>
        </div>
      )}
      {processState.executorState === "SUBMITTING" && (
        <div className="flex items-center justify-center h-full w-full">
          <div className="flex flex-col items-center gap-6 p-8 max-w-md">
            <LogoAnimation size={64} variant="spin" />

            {/* Text content */}
            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold text-gray-900">
                Submitting form...
              </h3>
              <p className="text-sm text-gray-500">
                Please wait while we process your information
              </p>
            </div>
          </div>
        </div>
      )}
      {processState.executorState === "SUBMIT_ERROR" && (
        <ApiError resetApiError={processState.resetApiError} />
      )}
      {processState.executorState === "PROCESS_ERROR" && <ProcessNotFound />}
    </>
  );
};
